import React, { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import classnames from 'classnames';
import { connect } from 'react-redux';

import * as Bixby from '$/core';
import { AppState } from '$/store';
import { GridService } from '$/services';
import { ReactComponent as PencilIcon } from '$/assets/icons/penciledit.svg';
import { saveClientEbitda } from '$/store/portfolio/actions';
import {
  getIsCurrentUserClientState,
  getIsCurrentUserCreditSightsAnalystState,
  getIsCurrentUserCreditSightsOpsState,
  getIsCurrentUserRatingsAnalystState,
  getIsCurrentUserSuFAnalystState,
} from '$/store/profile/selectors';
import './index.scss';

interface ICapitalStructureProps {
  currentCompany?: Bixby.Models.Company;
  isClient: boolean;
  isLoading: boolean;
  dispatchSaveClientEbitda: (company: Bixby.Models.Company, model: Bixby.Models.CompanyClientEbitda) => Promise<void>;
}

const CapitalStructure: React.FC<ICapitalStructureProps> = props => {
  const { currentCompany, isClient, isLoading, dispatchSaveClientEbitda } = props;
  const [rows, setRows] = useState([] as Bixby.Models.CompanyCapitalStructureRow[]);
  const [isEditing, setIsEditing] = useState(false);
  const [clientEbitda, setClientEbitda] = useState(
    currentCompany ? currentCompany.capitalStructure.clientEbitda : (null as number | null)
  );

  const hasPrivateDeclaration = useMemo(() => {
    return currentCompany?.declarationType === Bixby.Enums.DeclarationType.Private;
  }, [currentCompany]);

  useEffect(() => {
    const processCapitalStructureRows = () => {
      if (currentCompany && currentCompany.capitalStructure) {
        const unfilteredRows = GridService.buildManageCompanyCapitalStructureRows(
          currentCompany.capitalStructure,
          clientEbitda,
          isEditing
        );

        let rowIndex = 1;
        do {
          if (unfilteredRows[rowIndex].breakRow && unfilteredRows[rowIndex - 1].breakRow) {
            unfilteredRows.splice(rowIndex, 1);
          } else {
            rowIndex++;
          }
        } while (rowIndex < unfilteredRows.length);

        setRows(unfilteredRows);
      }
    };

    processCapitalStructureRows();
  }, [currentCompany, isEditing, clientEbitda]);

  const formatMultiplier = (value: number | null | undefined) => {
    if (value === null || value === undefined) {
      return '';
    }
    const percentage = value.toFixed(1);
    return value ? `${percentage}x` : '';
  };

  const formatPercentage = (value: number | null | undefined) => {
    if (value === null || value === undefined) {
      return '';
    }
    const percentage = (value * 100).toFixed(0);
    return value ? `${percentage}%` : '';
  };

  const formatLeverage = (value: number | null | undefined, index: number) => {
    if (value === null || value === undefined) {
      return '';
    }

    switch (index) {
      case 0:
        return Bixby.Utils.formatCurrency(value);

      case 1:
        if (value && isNaN(parseFloat(value as any))) {
          return value as any;
        } else {
          return formatPercentage(value);
        }

      default:
        return formatMultiplier(value);
    }
  };

  const borderRows = [
    Bixby.Enums.CapitalStructureRowTitles.FirstLienDebt,
    Bixby.Enums.CapitalStructureRowTitles.SecuredDebt,
    Bixby.Enums.CapitalStructureRowTitles.TotalDebt,
  ];

  const summaryRows = [Bixby.Enums.CapitalStructureRowTitles.AvailableLiquidity];

  const handleCancelClick = () => {
    setIsEditing(false);
    setClientEbitda(currentCompany ? currentCompany.capitalStructure.clientEbitda : null);
  };
  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleClientEbitdaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue: string = e.target.value;
    const rawValueSanitized = rawValue
      .replace(/\$/g, '')
      .replace(/,/g, '')
      .replace(/\(/g, '-')
      .replace(/\)/g, '')
      .replace(/\s/g, '');

    const parsedValue: number = parseFloat(rawValueSanitized);
    if (isNaN(parsedValue)) {
      setClientEbitda(null);
    } else {
      setClientEbitda(parsedValue);
    }
  };

  const handleSave = () => {
    if (currentCompany) {
      dispatchSaveClientEbitda(currentCompany, {
        clientEbitda: rows.find(i => i.key == Bixby.Enums.CompanyCapitalStructureKeys.Ebitda)?.clientEbitda,
        firstLienDebtGrossClientEbitda: rows.find(
          i => i.key == Bixby.Enums.CompanyCapitalStructureKeys.FirstLienDebtGross
        )?.clientEbitda,
        firstLienDebtNetClientEbitda: rows.find(i => i.key == Bixby.Enums.CompanyCapitalStructureKeys.FirstLienDebtNet)
          ?.clientEbitda,
        securedDebtGrossClientEbitda: rows.find(i => i.key == Bixby.Enums.CompanyCapitalStructureKeys.SecuredDebtGross)
          ?.clientEbitda,
        securedDebtNetClientEbitda: rows.find(i => i.key == Bixby.Enums.CompanyCapitalStructureKeys.SecuredDebtNet)
          ?.clientEbitda,
        totalDebtGrossClientEbitda: rows.find(i => i.key == Bixby.Enums.CompanyCapitalStructureKeys.TotalDebtGross)
          ?.clientEbitda,
        totalDebtNetClientEbitda: rows.find(i => i.key == Bixby.Enums.CompanyCapitalStructureKeys.TotalDebtNet)
          ?.clientEbitda,
        interestCoverageClientEbitda: rows.find(i => i.key == Bixby.Enums.CompanyCapitalStructureKeys.InterestCoverage)
          ?.clientEbitda,
        interestCoverageAfterCapitalExpendituresClientEbitda: rows.find(
          i => i.key == Bixby.Enums.CompanyCapitalStructureKeys.InterestCoverageAfterCapitalExpenditures
        )?.clientEbitda,
      } as Bixby.Models.CompanyClientEbitda);
    }
  };

  const tableRows = rows.map((row, index) => {
    const hasTopBorder = borderRows.includes(row.rowTitle);
    const hasSummaryBorder = summaryRows.includes(row.rowTitle);

    return (
      <tr key={index} className={classnames({ border_top: hasTopBorder, summary_border_top: hasSummaryBorder })}>
        <th>{row.rowTitle}</th>
        <td />
        <td className="center" data-raw={row.amount ? row.amount : ''}>
          {Bixby.Utils.formatCurrency(row.amount)}
        </td>

        {!hasPrivateDeclaration && (
          <>
            {isClient && isEditing && index == 0 && (
              <td className="center leverage_at_par client_ebitda_wrap">
                <input
                  type="number"
                  onChange={handleClientEbitdaChange}
                  value={clientEbitda || ''}
                  disabled={isLoading}
                  autoFocus
                />
              </td>
            )}
            {isClient && (!isEditing || index > 0) && (
              <td
                className="center leverage_at_par client_ebitda_wrap"
                data-raw={row.hasClientEbitda ? (row.clientEbitda ? row.clientEbitda : 'NM') : ''}>
                {row.hasClientEbitda ? formatLeverage(row.clientEbitda, index) || 'NM' : ''}
              </td>
            )}
          </>
        )}

        <td className="center leverage_at_par" data-raw={row.ebitda ? row.ebitda : ''}>
          {formatLeverage(row.ebitda, index)}
        </td>
        <td className="center leverage_at_par" data-raw={row.adjustedEbitda ? row.adjustedEbitda : ''}>
          {formatLeverage(row.adjustedEbitda, index)}
        </td>
        <td className="center leverage_at_par" data-raw={row.covenantEbitda ? row.covenantEbitda : ''}>
          {formatLeverage(row.covenantEbitda, index)}
        </td>
        <td className="center">{row.spread}</td>
        <td className="center" data-raw={row.maturity ? row.maturity : ''}>
          {row.maturity ? row.maturity : ''}
        </td>
        <td className="center">{row.agent}</td>
      </tr>
    );
  });
  return (
    <React.Fragment>
      <div className="top_wrap">
        <h2 className="heading_title">
          {currentCompany && currentCompany.capitalStructureProForma && (
            <span className="pro_forma">Pro Forma Capital Structure</span>
          )}
          {currentCompany && !currentCompany.capitalStructureProForma && <span>Capital Structure</span>}
          {currentCompany &&
            currentCompany.capitalStructure.clientEbitdaUpdatedBy &&
            currentCompany.capitalStructure.clientEbitdaUpdatedDate && (
              <span className="last_edited">
                Last Edited By {currentCompany.capitalStructure.clientEbitdaUpdatedBy} (
                {Bixby.Utils.formatByLineDateTime(currentCompany.capitalStructure.clientEbitdaUpdatedDate)})
              </span>
            )}
        </h2>
        <div className="top_options">
          {isClient && !hasPrivateDeclaration && (
            <div className="edit_wrap">
              {isEditing && (
                <React.Fragment>
                  <button
                    className={classnames('edit_btn', 'cancel_btn', { disabled: isLoading })}
                    onClick={handleCancelClick}
                    disabled={isLoading}>
                    Discard
                  </button>
                  <button
                    className={classnames('edit_btn', { disabled: isLoading })}
                    onClick={handleSave}
                    disabled={isLoading}>
                    Save
                  </button>
                </React.Fragment>
              )}
              {!isEditing && (
                <React.Fragment>
                  <div className="edit_ebitda_wrap">
                    <button className="edit_ebitda_btn" onClick={handleEditClick}>
                      Edit MyEBITDA
                      <div className="icon_wrap">
                        <PencilIcon />
                      </div>
                    </button>
                  </div>
                </React.Fragment>
              )}
            </div>
          )}
        </div>
      </div>
      <table
        className={classnames('table', 'capital_structure_table', { is_client: isClient && !hasPrivateDeclaration })}>
        <thead>
          <tr className="capital_structure_header_top">
            <th>($ in #, 000s)</th>
            <th />
            <th className="center">As of,</th>
            {isClient && !hasPrivateDeclaration && <th colSpan={4}>Leverage @ Par</th>}
            {(!isClient || (isClient && hasPrivateDeclaration)) && <th colSpan={3}>Leverage @ Par</th>}
            <th colSpan={3}>Loan Details</th>
          </tr>
        </thead>
        <thead>
          <tr className="capital_structure_header_bottom">
            <th>Capital Structure</th>
            <th />
            <th
              className="center"
              data-raw={
                currentCompany && currentCompany.capitalStructure.asOfDate
                  ? currentCompany.capitalStructure.asOfDate
                  : ''
              }>
              {currentCompany && currentCompany.capitalStructure.asOfDate
                ? Bixby.Utils.formatMonthYear(dayjs.utc(currentCompany.capitalStructure.asOfDate).toDate())
                : ''}
            </th>
            {isClient && !hasPrivateDeclaration && <th className="center">MyEBITDA</th>}
            <th className="center">EBITDA</th>
            <th className="center">Adj. EBITDA</th>
            <th className="center">COV. EBITDA</th>
            <th className="center">Spread</th>
            <th className="center">Maturity</th>
            <th className="center">Agent</th>
          </tr>
        </thead>
        <tbody>{tableRows}</tbody>
      </table>
    </React.Fragment>
  );
};

const mapStateToProps = (state: AppState) => {
  const { isCreditSightsAnalyst } = getIsCurrentUserCreditSightsAnalystState(state);
  const { isSuFAnalyst } = getIsCurrentUserSuFAnalystState(state);
  const { isRatingsAnalyst } = getIsCurrentUserRatingsAnalystState(state);

  const { isCreditSightsOps } = getIsCurrentUserCreditSightsOpsState(state);
  const { isClient } = getIsCurrentUserClientState(state);

  return {
    currentCompany: state.portfolioState.currentCompany,
    isClient: isClient && !isCreditSightsAnalyst && !isSuFAnalyst && !isRatingsAnalyst && !isCreditSightsOps,
    isLoading: state.portfolioState.isSavingClientEbitda,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchSaveClientEbitda: (company: Bixby.Models.Company, model: Bixby.Models.CompanyClientEbitda) =>
    dispatch(saveClientEbitda(company, model)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CapitalStructure);
